import AddIcon from "@mui/icons-material/Add";
import AttachmentIcon from "@mui/icons-material/AttachFile";
import { Box, Button, Grid, Stack, styled } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ROWS_PER_PAGE } from "../App";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import TimeOffCancelDialog from "../components/TimeOffCancelDialog";
import TimeOffCreateDialog from "../components/TimeOffCreateDialog";
import TimeOffDocumentationList from "../components/TimeOffDocumentationList";
import TimeoffEditDialog from "../components/TimeOffEditDialog";
import TimeOffSearchFilters from "../components/TimeOffSearchFilters";
import TimeOffStatusDialog from "../components/TimeOffStatusDialog";
import TimeoffViewDialog from "../components/TimeOffViewDialog";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import AlertComponent from "../external/ensolvers-core-frontend-mui/components/Common/AlertComponentMUI";
import {
  CheckboxComponent,
  DateField,
  SelectFieldComponent,
} from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useTimeOffList, { TimeOffSortField } from "../hooks/useTimeOffList";
import { Documentation } from "../models/Documentation";
import Timeoff from "../models/Timeoff";
import { TimeOffType, timeoffOptions } from "../types/TimeOffType";
import CustomDateFnsUtils from "../utils/CustomDateFnsUtils";
import { TimeOffStatus } from "../utils/enums/TimeOffStatus";
import OpsDateUtils from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import TimeOffUtils from "../utils/TimeOffUtils";
import { useState } from "react";
import GanttChart from "../components/GanttChart";
import useTableHeight from "../hooks/useTableHeight";
import { makeStyles } from "@mui/styles";
import ActionButton from "../components/Worklog/ActionButton";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { Token } from "../theme";
import { TimeOffStatusChip, isPendingStatus, statusTone } from "../components/TimeOff/TimeOffStatusChip";
import { Num } from "../components/Num";

const Input = styled("input")({
  display: "none",
});

export default function TimeoffList() {
  const { t } = useTranslation();
  const {
    timeoffState,
    clearSelectedFiles,
    handleUploadDocument,
    handleDeleteDocumentIcon,
    getAdditionalActions,
    getAdditionalActionsIcons,
    openViewDialog,
    closeViewDialog,
    closeEditDialog,
    setSelectedFiles,
    isEditing,
    userExternalId,
    selectedFiles,
    isLoading,
    openCreateModal,
    createForOthers,
    userCanResolveRequest,
    setIsEditing,
    onDataFetch,
    forceUpdateOn,
    year,
    setYear,
    month,
    setMonth,
    setCreateForOthers,
    setOpenCreateModal,
    searchFilter,
    statusFilter,
    dateFilter,
    projects,
    projectSearch,
    setSearchFilter,
    setStatusFilter,
    setDateFilter,
    setProjectSearch,
    getUserWorkLogs,
    handleMissingDocumentationRequested,
    isGanttGraphEnabled,
    usernames,
    status,
    queryDate,
    search,
  } = useTimeOffList();
  const classes = useStyles();

  const [isChartOpen, setIsChartOpen] = useState<boolean>(false);
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const [editTimeoff, setEditTimeoff] = useState<Timeoff | undefined>();
  const [selectedTimeOff, setSelectedTimeOff] = useState<Timeoff | undefined>(undefined);

  const openEditDialog = (instance: Timeoff) => {
    setEditTimeoff(instance);
    setSelectedFiles(instance.documentation);
  };

  const renderBody = () => {
    const onCreateTimeoff = async (timeoff: Timeoff) => {
      await timeoffState.createTimeOff(timeoff);
      clearSelectedFiles();
      let requesterExternalId =
        RoleUtils.isAdminOrManager() && timeoff.requesterExternalId === ""
          ? userExternalId
          : RoleUtils.isAdminOrManager()
            ? timeoff.requesterExternalId
            : userExternalId;
      await getUserWorkLogs(requesterExternalId!, timeoff.shortStartDate, timeoff.shortEndDate);
    };
    const onOpenViewDialog = async (timeoff: Timeoff) => openViewDialog(timeoff);
    const onCloseViewDialog = () => closeViewDialog();
    const onCloseTimeoffEditDialog = () => closeEditDialog();
    const onSaveTimeOffEditDialog = async (timeOff: Timeoff) => {
      await timeoffState.updateTimeOff(timeOff);
      clearSelectedFiles();
      await getUserWorkLogs(timeOff.requesterExternalId, timeOff.shortStartDate, timeOff.shortEndDate);
    };

    const onCloseTimeOffCreateDialog = () => {
      setOpenCreateModal(false);
      setCreateForOthers(false);
      timeoffState.clearTimeoff();
    };
    const onSaveTimeOffCreateDialog = async (timeoff: Timeoff) => {
      await timeoffState.createTimeOff(timeoff);
      clearSelectedFiles();
    };
    const onHandleApprovalTimeoff = async (data: Timeoff, status: TimeOffStatus) => timeoffState.handleApprovalTimeOff(data, status);
    const canEditDocumentation = (instance: Timeoff) => RoleUtils.isAdminOrManager() || instance.requesterExternalId === userExternalId;

    const getDialogContent = (register: any, errors: any, control: any, instance?: Timeoff): JSX.Element => {
      if (instance) {
        setSelectedFiles(instance.documentation);
      }

      return (
        <>
          {RoleUtils.isAdminOrManager() && !instance && (
            <>
              <SelectFieldComponent
                control={control}
                register={register}
                errors={errors}
                propertyName={instance ? "requester.externalId" : "requesterExternalId"}
                label={"Member"}
                required={false}
                options={usernames
                  .filter((v, i, self) => i === self.findIndex((t) => t.externalId === v.externalId && t.label === v.label))
                  .map((u) => ({ label: u.label, value: u.externalId }))}
                defaultValue={usernames.find((u) => u.externalId === userExternalId)?.externalId}
              />
              <label style={{ display: "block", paddingLeft: ".6em", marginBottom: "1em" }}>
                {RoleUtils.isAdmin()
                  ? "An administrator can create TimeOffs for all members of all teams. If you want to create a Timeoff for yourself, just leave the input empty."
                  : RoleUtils.isManager()
                    ? "A manager can create TimeOffs for members assigned to their projects. If you want to create a Timeoff for yourself, just leave the input empty."
                    : null}
              </label>
            </>
          )}

          <SelectFieldComponent
            control={control}
            register={register}
            errors={errors}
            propertyName={"type"}
            label={"Time Off Type"}
            required={true}
            options={timeoffOptions}
          />
          <DateField
            control={control}
            register={register}
            errors={errors}
            propertyName={"shortStartDate"}
            label="Start Date"
            required={true}
            utils={CustomDateFnsUtils}
          />
          <DateField
            control={control}
            register={register}
            errors={errors}
            propertyName={"shortEndDate"}
            label="End Date"
            required={true}
            utils={CustomDateFnsUtils}
          />

          <CheckboxComponent
            control={control}
            register={register}
            errors={errors}
            propertyName={"halfDay"}
            label="Half day"
            required={false}
          />

          <Grid>
            <TimeOffDocumentationList
              viewTimeoff={timeoffState.viewTimeoff}
              selectedFiles={selectedFiles}
              onDelete={(files: Documentation[]) => handleDeleteDocumentIcon(files)}
            />
            <Stack direction="row">
              <label htmlFor="contained-button-file">
                <Input
                  id="contained-button-file"
                  type="file"
                  accept=".pdf"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUploadDocument(e)}
                />
                <SpinnerWrapper isLoading={isLoading}>
                  <Button variant="contained" component="span" sx={{ margin: ".7em" }}>
                    Add document
                  </Button>
                </SpinnerWrapper>
              </label>
            </Stack>
          </Grid>
        </>
      );
    };

    return (
      <Box sx={{ borderRadius: 8 }}>
        <TableBasedCrud<Timeoff>
          gridButtonContainer={gridButtonContainer}
          maxHeight={calculateMaxHeight(true)}
          stickyHeader
          stickyPagination
          elementName={"Timeoff Request"}
          rowsPerPage={ROWS_PER_PAGE}
          denseTable
          stylesForRowIfCondition={(instance) =>
            isPendingStatus(instance.status) ? { boxShadow: `inset 3px 0 0 ${statusTone(instance.status).fg}` } : undefined
          }
          delayFetch={searchFilter}
          addButtonBuilder={() => <></>}
          buttonContainer={{ display: "none" }}
          disableAutoFetch
          onDataFetch={onDataFetch}
          onCreate={onCreateTimeoff}
          onViewMode={onOpenViewDialog}
          forceUpdateOn={forceUpdateOn}
          renderDialogContent={(register, errors, control, instance) =>
            getDialogContent(register, errors, control, isEditing ? instance : undefined)
          }
          onCancelDialog={clearSelectedFiles}
          setIsEditModeActive={setIsEditing}
          tableColumns={[
            // Only the manager will see the Requester name — use spread instead of null to keep column indices stable
            ...(userCanResolveRequest
              ? [
                  {
                    header: t("Requested by"),
                    content: (instance: Timeoff) => (
                      <>
                        {instance.requester?.firstName} {instance.requester?.lastName}
                      </>
                    ),
                  },
                ]
              : []),
            {
              header: t("Created at"),
              content: (instance) => <Num>{OpsDateUtils.ensureFormatDateUTC(instance.createdAt)}</Num>,
              orderBy: TimeOffSortField.CREATED_AT,
            },
            {
              header: t("Start date"),
              content: (instance) => <Num>{OpsDateUtils.ensureFormatDateUTC(instance.shortStartDate)}</Num>,
              orderBy: TimeOffSortField.SHORT_START_DATE,
            },
            {
              header: t("End date"),
              content: (instance) => <Num>{OpsDateUtils.ensureFormatDateUTC(instance.shortEndDate)}</Num>,
              orderBy: TimeOffSortField.SHORT_END_DATE,
            },
            {
              header: t("Days"),
              content: (instance) => <Num>{instance.totalDays}</Num>,
            },
            {
              header: t("Type"),
              content: (instance) => <>{instance.type === TimeOffType.VACATION ? "PLANNED VACATION" : TimeOffType[instance.type]}</>,
            },
            {
              header: t("Status"),
              content: (instance) => (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <TimeOffStatusChip status={instance.status} />
                </div>
              ),
            },
            {
              header: "Resolution By",
              content: (instance) => (
                <>
                  {instance.approvedBy?.firstName} {instance.approvedBy?.lastName}
                </>
              ),
            },
            {
              header: "Resolution Date",
              content: (instance) => (
                <>{instance.approvedDate ? OpsDateUtils.formatInUTCAsDDMMMYYY(instance.approvedDate) : <label>-</label>}</>
              ),
            },
            {
              header: t("Attachments"),
              content: (instance) => (
                <label style={{ padding: "0.75em" }}>{instance.documentation ? instance.documentation.length : 0}</label>
              ),
            },
            {
              header: t("Actions"),
              content: (instance) => {
                const actions = getAdditionalActionsIcons(instance);
                return (
                  <>
                    {actions.map((action) => {
                      return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
                    })}
                  </>
                );
              },
            },
          ]}
          buildAdditionalActions={getAdditionalActions}
          hideActions={true} // Hide default Actions column
          validateBeforeClosing={true}
          resetValue={{ type: null, shortStartDate: null, shortEndDate: null, halfDay: false }}
        />
        <GetCancelStatusDialog
          cancelRequestTimeOff={timeoffState.cancelRequestTimeOff}
          setCancelRequestTimeOff={timeoffState.setCancelRequestTimeOff}
          handleOnChangeTimeOffStatus={timeoffState.handleOnChangeTimeOffStatus}
        />

        <TimeOffCreateDialog
          userExternalId={userExternalId ? userExternalId : ""}
          selectedFiles={selectedFiles}
          onClose={onCloseTimeOffCreateDialog}
          onSave={onSaveTimeOffCreateDialog}
          forOthers={createForOthers}
          handleUploadDocument={handleUploadDocument}
          handleDeleteDocument={handleDeleteDocumentIcon}
          isLoading={isLoading}
          usernames={usernames}
          open={openCreateModal}
        />

        {timeoffState.timeoff && (
          <TimeOffStatusDialog
            title={
              "Timeoff request - " +
              timeoffState.timeoff.type +
              " - " +
              timeoffState.timeoff.totalDays +
              (timeoffState.timeoff.totalDays > 1 ? " days" : " day")
            }
            message={TimeOffUtils.timeoffStatusMessage(timeoffState.timeoff)}
            timeoff={timeoffState.timeoff}
            onClose={timeoffState.clearTimeoff}
            handleApproval={onHandleApprovalTimeoff}
            handleExtraFunction={() => timeoffState.handleOnChangeTimeOffStatus(timeoffState.timeoff!, TimeOffStatus.ADD_DOCUMENTATION)}
            extraButton
            missingDocumentation={handleMissingDocumentationRequested(timeoffState.timeoff)}
          />
        )}
        <TimeoffViewDialog viewTimeoff={timeoffState.viewTimeoff} selectedFiles={selectedFiles} onClose={onCloseViewDialog} />

        <TimeoffEditDialog
          editTimeoff={timeoffState.editTimeoff}
          selectedFiles={selectedFiles}
          handleDeleteDocument={handleDeleteDocumentIcon}
          isLoading={isLoading}
          onClose={onCloseTimeoffEditDialog}
          onSave={onSaveTimeOffEditDialog}
          handleUploadDocument={handleUploadDocument}
          canEditDocumentation={canEditDocumentation}
        />

        <TimeOffCancelDialog
          selectedTimeOff={timeoffState.selectedTimeOff}
          onClose={timeoffState.clearSelectedTimeoff}
          handleOnCancelTimeOff={(data: Timeoff, status: TimeOffStatus) => timeoffState.handleOnCancelTimeOff(data, status)}
        />
      </Box>
    );
  };

  const headerActions = (
    <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
      <Button
        startIcon={<AddIcon />}
        variant="contained"
        disableElevation
        onClick={() => setOpenCreateModal(true)}
        sx={{ backgroundColor: Token.actionPrimary, color: Token.textOnAction }}
      >
        Add for me
      </Button>
      {RoleUtils.isAdminOrManager() && (
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          disableElevation
          onClick={() => {
            setOpenCreateModal(true);
            setCreateForOthers(true);
          }}
          sx={{ backgroundColor: Token.actionPrimary, color: Token.textOnAction }}
        >
          Add for others
        </Button>
      )}
    </Box>
  );

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <GetHeader
          headerActions={headerActions}
          searchFilter={search ? search : searchFilter}
          statusFilter={status ? status : statusFilter}
          dateFilter={queryDate ? queryDate : dateFilter}
          projects={projects}
          projectSearch={projectSearch}
          setSearchFilter={setSearchFilter}
          setStatusFilter={setStatusFilter}
          setDateFilter={setDateFilter}
          setProjectSearch={setProjectSearch}
          onToggleChart={() => setIsChartOpen((prev) => !prev)}
          isChartOpen={isChartOpen}
          isGanttGraphEnabled={isGanttGraphEnabled!!}
          year={year}
          month={month}
          setYear={setYear}
          setMonth={setMonth}
        />
      }
      bottomSection={
        isChartOpen ? (
          <GanttChart
            filters={[searchFilter, statusFilter, dateFilter, projectSearch]}
            fetchData={() => timeoffState.getTimeOff(0, 99, true, null, null)}
          />
        ) : (
          renderBody()
        )
      }
    />
  );
}

interface GetHeaderProps {
  headerActions?: React.ReactNode;
  searchFilter: string;
  statusFilter: string;
  dateFilter: string;
  projects: AutoCompleteUIDTO[];
  projectSearch: AutoCompleteUIDTO | null;
  setSearchFilter: Dispatch<SetStateAction<string>>;
  setStatusFilter: Dispatch<SetStateAction<string>>;
  setDateFilter: Dispatch<SetStateAction<string>>;
  setProjectSearch: Dispatch<SetStateAction<AutoCompleteUIDTO | null>>;
  isChartOpen: boolean;
  onToggleChart: () => void;
  isGanttGraphEnabled: boolean;
  month: Date | null;
  setMonth: (date: Date | null) => void;
  year: Date | null;
  setYear: (date: Date | null) => void;
}

const GetHeader = ({
  headerActions,
  searchFilter,
  statusFilter,
  dateFilter,
  projects,
  projectSearch,
  setSearchFilter,
  setStatusFilter,
  setDateFilter,
  isChartOpen,
  setProjectSearch,
  onToggleChart,
  isGanttGraphEnabled,
  year,
  setYear,
  month,
  setMonth,
}: GetHeaderProps) => {
  const handleSearchFilter = (value: string) => setSearchFilter(value);
  const handleStatusFilter = (status: string) => setStatusFilter(status);
  const handleDateFilter = (date: string) => setDateFilter(date);
  const handleProjectFilter = (project: AutoCompleteUIDTO) => setProjectSearch(project);

  return (
    <TimeOffSearchFilters
      headerActions={headerActions}
      searchFilter={searchFilter}
      statusFilter={statusFilter}
      dateFilter={dateFilter}
      projects={projects}
      projectSearch={projectSearch!}
      handleSearchFilter={handleSearchFilter}
      handleStatusFilter={handleStatusFilter}
      handleDateFilter={handleDateFilter}
      handleProjectFilter={handleProjectFilter}
      onToggleChart={onToggleChart}
      isChartOpen={isChartOpen}
      isGanttGraphEnabled={isGanttGraphEnabled}
      year={year}
      month={month}
      setYear={setYear}
      setMonth={setMonth}
    />
  );
};

interface CancelStatusDialogProps {
  cancelRequestTimeOff: Timeoff | undefined;
  setCancelRequestTimeOff: Dispatch<SetStateAction<Timeoff | undefined>>;
  handleOnChangeTimeOffStatus: (data: Timeoff, status: string) => void;
}

const GetCancelStatusDialog = ({ cancelRequestTimeOff, setCancelRequestTimeOff, handleOnChangeTimeOffStatus }: CancelStatusDialogProps) => {
  const handleCancelRequest = (data: Timeoff, status: string) => {
    if (!cancelRequestTimeOff) return;
    cancelRequestTimeOff.statusReason = data.statusReason;
    handleOnChangeTimeOffStatus(cancelRequestTimeOff, status);
  };

  if (cancelRequestTimeOff) {
    return (
      <AlertComponent
        title={`Cancel request - ${cancelRequestTimeOff.requester.firstName} ${cancelRequestTimeOff.requester.lastName} /
        ${OpsDateUtils.formatInUTCAsDDMMMYYY(cancelRequestTimeOff.shortStartDate)}
        - ${OpsDateUtils.formatInUTCAsDDMMMYYY(cancelRequestTimeOff.shortEndDate)}  `}
        message={`Approving the request will change the status to CANCELLED,
                denying it, will reset to APPROVED state.`}
        confirmLabel={"APPROVE CANCELLATION"}
        onConfirm={() => handleCancelRequest(cancelRequestTimeOff, TimeOffStatus.CANCELLED)}
        cancelLabel={"REJECT CANCELLATION"}
        onCancel={() => handleCancelRequest(cancelRequestTimeOff, TimeOffStatus.APPROVED)}
        onClose={() => setCancelRequestTimeOff(undefined)}
      />
    );
  }

  return null;
};
const useStyles = makeStyles(
  (theme) => ({
    addButton: {
      top: "4px",
      [theme.breakpoints.down(600)]: {
        position: "absolute",
        top: "10px",
        right: "0px",
      },
    },
  }),
  { index: 1 },
);
